import { useEffect, useRef, useState } from "react";
import { AccountConfig, PermissionEnum } from "TypesAndInterfaces";
import Button from "components/Button/Button";
import InputField from "components/InputField/InputField";
import { IFailedInvoicesQuery, getFailedInvoicesMarketplaceQuery, getFailedInvoicesQuery } from "../graphql";
import { useLazyQuery } from "@apollo/client";
import { displayError } from "utils/displayNotification";
import "./ControlSyncs.scss";
import { InvoicesTable } from "./InvoicesTable";
import usePermission from "hooks/usePermission";
import OperatorPermissionCheck from "pages/OperatorPermissionCheck/components/OperatorPermissionCheck";

interface IProps {
  disabled: boolean;
  config: AccountConfig;
  handleShopSync: (ids: string) => void;
  handleRetryInvoices: (ids: string) => void;
  handleRestartInvoices: (ids: string) => void;
  marketplaceId?: number
}
enum SyncType {
  Shop = "Shop",
  Invoice = "Invoice",
}

const ControlSyncs = ({
  config,
  disabled,
  handleRetryInvoices,
  handleShopSync,
  handleRestartInvoices,
  marketplaceId
}: IProps) => {
  const [displaySync, setDisplaySync] = useState(true);
  const [displayOperatorPermissionCheck, setDisplayOperatorPermissionCheck] = useState(true);
  const shopSyncRef = useRef(null);
  const [shopIds, setShopIds] = useState("");
  const [invoiceIds, setInvoiceIds] = useState("");
  const [data, setData] = useState<IFailedInvoicesQuery[]>([]);
  const { isAuthorized } = usePermission(PermissionEnum.MANAGE_SHOPS);
  const adminView = marketplaceId ? true : false;

  const [getFailedInvoices, { refetch: refetchFailedInvoices }] = useLazyQuery<
  { getFailedInvoices: IFailedInvoicesQuery[] },
  { }
>(getFailedInvoicesQuery, {
  fetchPolicy: "no-cache",
  notifyOnNetworkStatusChange: true,
  onCompleted: (_data) => {
    if (_data) {
      console.log(_data);
      setData(_data.getFailedInvoices);
    }
  },
  onError: (e) => {
    displayError(e.message);
  },
});

const [getFailedInvoicesMarketplace, { refetch: refetchFailedInvoicesMarketplace }] = useLazyQuery<
  { getFailedInvoices: IFailedInvoicesQuery[] },
  { marketplaceId?: number }
>(getFailedInvoicesMarketplaceQuery, {
  fetchPolicy: "no-cache",
  notifyOnNetworkStatusChange: true,
  onCompleted: (_data) => {
    if (_data) {
      console.log(_data);
      setData(_data.getFailedInvoices);
    }
  },
  onError: (e) => {
    displayError(e.message);
  },
});

  useEffect(() => {
    if(marketplaceId) {
      getFailedInvoicesMarketplace({
        variables: {  marketplaceId  },
      });
    } else {
      getFailedInvoices();
    }
  }, [config, getFailedInvoices, getFailedInvoicesMarketplace, marketplaceId]);

  const unvalidateCommas = (type: SyncType) => {
    const data = type === SyncType.Shop ? shopIds : invoiceIds;
    const result = data.match(/[^,]+/g)?.join();
    if (result) {
      switch (type) {
        case SyncType.Shop:
          setShopIds(result);
          handleShopSync(result);
          break;
        case SyncType.Invoice:
          setInvoiceIds(result);
          handleRestartInvoices(result);
          break;
        default:
          break;
      }
    }
  };

  const buttonClass = adminView 
  ? "button--bordered button--green button-less-padding admin-view-class" 
  : "button--bordered button--green button-less-padding";

  return (
    <main>
      {isAuthorized && (
        <div>
          <div className="syncs-container">
            <h3 onClick={() => setDisplaySync(!displaySync)}>
              Synchronize Shops
              <small
                className={displaySync ? "icon-angle-up" : "icon-angle-down"}
              />
            </h3>
            {displaySync && (
              <span className="schedule-container">
                <InputField
                  innerRef={shopSyncRef}
                  numeric
                  commaList
                  label="Shop IDs"
                  value={shopIds}
                  onChange={(e) => setShopIds(e.currentTarget.value)}
                  className="shop-ids-input"
                />
                <Button
                  className={buttonClass}
                  type="button"
                  content="Start Shop Sync"
                  icon="sync"
                  onClick={() => unvalidateCommas(SyncType.Shop)}
                  disabled={disabled}
                ></Button>
              </span>
            )}
          </div>
          <div className="syncs-container">
            <h3 onClick={() => setDisplayOperatorPermissionCheck(!displayOperatorPermissionCheck)}>
              Check Operator Permissions
              <small
                className={displayOperatorPermissionCheck ? "icon-angle-up" : "icon-angle-down"}
              />
            </h3>
            {displayOperatorPermissionCheck && (<OperatorPermissionCheck marketplaceId={marketplaceId}/>)}
          </div>
        </div>
      )}
      <div>
        {data.length > 0 && (
          <InvoicesTable
            data={data}
            handleRestartInvoices={handleRestartInvoices}
            handleRetryInvoices={handleRetryInvoices}
            disableButtons={disabled}
            refetch={marketplaceId ? refetchFailedInvoicesMarketplace : refetchFailedInvoices}
            adminView={adminView}
          />
        )}
      </div>
    </main>
  );
};

export default ControlSyncs;
